"use client";

import { LoginModal } from "@/components/auth/login-modal";
import { CarouselItem } from "@/components/shadcn/carousel";
import { useTranslations } from "next-intl";
import { isCarouselEvent } from "../_actions/utils/compare-events";
import { CompletedEventsTable } from "./completed-events-table";
import { EventCarouselSection } from "./event-carousel-section";
import { useEventForm } from "./event-form-context";
import { EventLinkAutoRefresh } from "./event-link-auto-refresh";
import { GiveawayRegistrationModal } from "./giveaway/giveaway-registration-modal";
import { GiveawayUpcomingCard } from "./giveaway/giveaway-upcoming-card";
import { GiveawayEventWithUserStatus } from "./giveaway/types";
import { PremiumRequiredModal } from "./premium-required-modal";
import { formatInstructorName } from "./webinar/format-instructor-name";
import { WebinarEventWithUserStatus } from "./webinar/types";
import { WebinarRegistrationModal } from "./webinar/webinar-registration-modal";
import { WebinarUpcomingCard } from "./webinar/webinar-upcoming-card";

export function EventsPageClient({
    webinarEvents,
    giveawayEvents,
}: {
    webinarEvents: WebinarEventWithUserStatus[];
    giveawayEvents: GiveawayEventWithUserStatus[];
}) {
    const t = useTranslations("events");
    const { activeModal, modalData, closeModal } = useEventForm();

    // Carousels show every active/upcoming event; ended ones go into the
    // tables below.
    const carouselGiveaways = giveawayEvents.filter(isCarouselEvent);
    const carouselWebinars = webinarEvents.filter(isCarouselEvent);
    const endedGiveaways = giveawayEvents.filter((e) => !isCarouselEvent(e));
    const endedWebinars = webinarEvents.filter((e) => !isCarouselEvent(e));

    return (
        <>
            <div className="min-h-screen">
                <EventLinkAutoRefresh
                    webinarEvents={webinarEvents}
                    giveawayEvents={giveawayEvents}
                />
                <div className="flex w-full flex-col gap-6 sm:gap-8">
                    <EventCarouselSection
                        title={t("prizedGiveaways")}
                        description={t("giveawaysDescription")}
                        emptyMessage={t("noGiveaways")}
                        isEmpty={carouselGiveaways.length === 0}
                        className="bg-primary/10"
                    >
                        {carouselGiveaways.map((event) => (
                            <CarouselItem key={event.id}>
                                <GiveawayUpcomingCard event={event} />
                            </CarouselItem>
                        ))}
                    </EventCarouselSection>

                    <EventCarouselSection
                        title={t("plannedWebinars")}
                        description={t("webinarsDescription")}
                        emptyMessage={t("noWebinars")}
                        isEmpty={carouselWebinars.length === 0}
                        className="bg-primary/20"
                    >
                        {carouselWebinars.map((event) => (
                            <CarouselItem
                                key={event.id}
                                className="lg:basis-1/2"
                            >
                                <WebinarUpcomingCard event={event} />
                            </CarouselItem>
                        ))}
                    </EventCarouselSection>

                    <div className="grid items-start gap-6 lg:grid-cols-2 lg:gap-8">
                        <CompletedEventsTable
                            title={t("completedWebinars")}
                            eventColumnLabel={t("webinar")}
                            rows={endedWebinars.map((event) => ({
                                id: event.id,
                                date: event.date,
                                title: event.title,
                                subtitle: `${t("trainer")}: ${formatInstructorName(event.instructor)}`,
                                isPremium: event.isPremium,
                                capacity: event.capacity,
                            }))}
                        />
                        <CompletedEventsTable
                            title={t("completedGiveaways")}
                            eventColumnLabel={t("giveaway")}
                            rows={endedGiveaways.map((event) => ({
                                id: event.id,
                                date: event.date,
                                title: event.title,
                                subtitle: event.description,
                                isPremium: event.isPremium,
                                capacity: event.capacity,
                            }))}
                        />
                    </div>
                </div>
            </div>

            {/* Centralized modals */}
            {modalData && (
                <>
                    <WebinarRegistrationModal
                        open={activeModal === "webinar"}
                        onOpenChange={(open) => !open && closeModal()}
                        eventId={modalData.eventId}
                        eventTitle={modalData.eventTitle}
                    />
                    <GiveawayRegistrationModal
                        open={activeModal === "giveaway"}
                        onOpenChange={(open) => !open && closeModal()}
                        eventId={modalData.eventId}
                        eventTitle={modalData.eventTitle}
                    />
                </>
            )}

            {/* Auth modals */}
            <LoginModal
                open={activeModal === "loginRequired"}
                onOpenChange={(open) => !open && closeModal()}
            />
            <PremiumRequiredModal
                open={activeModal === "premiumRequired"}
                onOpenChange={(open) => !open && closeModal()}
            />
        </>
    );
}
